import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export const ImageLightbox: React.FC<{ url: string; contentType?: string; onClose: () => void }> = ({ url, contentType, onClose }) => {
  const isImage = (contentType || '').startsWith('image/');
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Render using portal to escape parent container constraints
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-95" onClick={onClose}>
      {isImage ? (
        <>
          {/* Close button */}
          <button 
            onClick={onClose}
            className="fixed top-4 right-4 z-[10000] text-white hover:text-gray-300 transition-colors"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Loading indicator */}
          {!imageLoaded && (
            <div className="fixed inset-0 flex items-center justify-center z-[9999]">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
            </div>
          )}
          
          {/* Full screen image with responsive padding */}
          <div className="fixed inset-4 sm:inset-8 md:inset-12 z-[9999] flex items-center justify-center">
            <img 
              src={url} 
              alt="preview" 
              className={`max-w-full max-h-full object-contain transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onClick={(e) => e.stopPropagation()}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)}
            />
          </div>
        </>
      ) : (
        // Non-image files - centered modal
        <div className="fixed inset-0 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-semibold">File Preview</div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 text-center">
              <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 underline">
                Open file in new tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};


